import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { closeModal, closeModalWithDelay } from '../../../redux/slices/modalSlice'
import { useTranslation } from '../../../hooks/useTranslation'
import { RootState } from '../../../redux/store'
import { LoginStatus } from '../../../redux/slices/userSlice'
import { API } from '../../../core/API'
import { Link, useLocation } from 'react-router-dom'
import { TLevel, TUserProfile } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import Utils from '../../../core/Utils'
import { ModalType } from '../../../core/enums/Modals'
import { Smartico } from '../../../core/Smartico'
import { PopoverAnchor } from '../../Header/Header'
import { ChevronRightIcon, LogoutIcon, PencilIcon, UserIcon } from '../../../img/svgComponents/UiIcons'

interface UserModalProps {
	position?: PopoverAnchor
}

const POPOVER_WIDTH = 380
const EDGE_GAP = 12

const UserModal = ({ position }: UserModalProps) => {
	const isMobile = Utils.isMobile()
	const dispatch = useAppDispatch()
	const {
		modalProps: { isVisible },
	} = useAppSelector((state: RootState) => state.modal)

	const { loggedIn, user } = useAppSelector((state: RootState) => state.user)
	const profile = useAppSelector((state: RootState) => state.userPublic.profile)
	const levels = useAppSelector((state: RootState) => state.level.levels)
	const location = useLocation()
	const isFirstRender = useRef(true)
	const [progressLevel, setProgressLevel] = useState<TLevel>()
	const [isFinalLevel, setIsFinalLevel] = useState<boolean>(false)
	const [currentLevel, setCurrentLevel] = useState<TLevel>()

	useEffect(() => {
		if (profile && levels) {
			setCurrentLevel(levels.find((level: TLevel) => level.id === profile.ach_level_current_id))
		}
	}, [profile, levels])

	const nextLevel = useMemo(() => {
		if (levels && profile) {
			const currentLevelIndex = levels.findIndex((lvl) => lvl.id === profile.ach_level_current_id)
			if (currentLevelIndex !== -1) {
				if (currentLevelIndex < levels.length - 1) {
					return levels[currentLevelIndex + 1]
				}
			}
		}
		return null
	}, [levels, profile])

	const getProgressLevel = (allLevels: TLevel[], userInfo: TUserProfile) => {
		const currentLevelIndex = allLevels.findIndex((lvl) => lvl.id === userInfo.ach_level_current_id)
		if (currentLevelIndex !== -1) {
			if (currentLevelIndex < allLevels.length - 1) {
				const nextLevel = allLevels[currentLevelIndex + 1]
				setProgressLevel(nextLevel)
			} else {
				setIsFinalLevel(true)
			}
		}
	}

	const progressPercentage = useMemo(() => {
		if (currentLevel && progressLevel && profile) {
			const rangeStart = currentLevel.required_points
			const rangeEnd = progressLevel.required_points
			const userPoints = profile.ach_points_ever
			if (rangeEnd > rangeStart) {
				return Math.min(((userPoints - rangeStart) / (rangeEnd - rangeStart)) * 100, 100)
			}
		}
		return 0
	}, [currentLevel, progressLevel, profile])

	const modalRef = useRef<HTMLDivElement>(null)
	const handleCloseModal = () => {
		dispatch(closeModalWithDelay())
	}

	const { getTexts } = useTranslation(true)

	useOutsideClick(modalRef, handleCloseModal, ModalType.User)

	const handleClose = (f?: () => void) => {
		if (f) {
			f()
		}
	}

	const doLogoutUser = () => {
		if (loggedIn === LoginStatus.LoggedIn && user) {
			API.logout()
			dispatch(closeModal())
		}
	}

	// Kept inside the viewport with the arrow pointing at the level pill
	const width = Math.min(POPOVER_WIDTH, window.innerWidth - EDGE_GAP * 2)
	const left = position ? Math.max(EDGE_GAP, Math.min(position.anchorX - width / 2, window.innerWidth - width - EDGE_GAP)) : 0
	const modalStyle = position
		? ({ top: position.top, left, '--arrow-x': `${position.anchorX - left}px` } as React.CSSProperties)
		: { right: '12px' }

	useEffect(() => {
		if (levels && profile) {
			getProgressLevel(levels, profile)
		}
	}, [levels, profile])

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false
			return
		}
		dispatch(closeModal())
	}, [location, dispatch])

	const displayName =
		profile?.public_username && !user?.user_ext_id?.endsWith(profile.public_username)
			? profile.public_username
			: user?.profile?.firstName || profile?.public_username

	return (
		<div className={`user-modal ${isVisible ? 'animated' : ''}`} style={modalStyle} ref={modalRef}>
			<div className="user-info">
				<button
					type="button"
					className="user-avatar"
					aria-label="Change avatar"
					style={{ backgroundImage: `url('${profile?.avatar_url}')` }}
					onClick={() => Smartico.dp(`dp:gf_change_avatar&hide_main=true`)}
				/>
				<div className="wrap">
					<div className="user-name-row">
						<span className="user-name">{displayName}</span>
						<button type="button" className="edit-btn" aria-label="Change nickname" onClick={() => Smartico.dp(`dp:gf_change_nickname&hide_main=true`)}>
							<PencilIcon size={18} />
						</button>
					</div>
					<button type="button" className="level" onClick={() => Smartico.dp(`dp:gf_levels&id=${currentLevel?.id}&hide_main=true`)}>
						{currentLevel?.name}
					</button>
				</div>
			</div>
			<div className="tier-progress">
				<div className="tier-progress-head">
					<span>Tier progress</span>
					<span className="tier-progress-points">
						{(profile?.ach_points_ever || 0).toLocaleString('en-US')} / {((isFinalLevel ? profile?.ach_points_ever : nextLevel?.required_points) || 0).toLocaleString('en-US')}
					</span>
				</div>
				<div className="progress-bar">
					<div style={{ width: `${isFinalLevel ? 100 : progressPercentage}%` }} />
				</div>
			</div>
			<div className="user-links">
				{!isMobile && (
					<Link className="user-link" to="/profile" onClick={() => dispatch(closeModal())}>
						<UserIcon size={30} />
						<span>{getTexts('profile')}</span>
						<ChevronRightIcon className="chevron" size={22} />
					</Link>
				)}
				<button type="button" className="user-link logout" onClick={() => handleClose(doLogoutUser)}>
					<LogoutIcon size={28} />
					<span>{getTexts('logOut')}</span>
					<ChevronRightIcon className="chevron" size={22} />
				</button>
			</div>
		</div>
	)
}

export default UserModal
